/**
 * Patient details drawer — shown from Open Patient / the Queue "View Patient"
 * action for users WITHOUT Patient Workspace access (PRD.287 follow-up).
 *
 * Mirrors the Patient Workspace sidebar Overview look (header + cards) but is
 * self-contained and view-oriented: the workspace-only actions (record vitals,
 * open invoices, edit intake, etc.) navigate into `/patient/:id`, which these
 * roles can't reach, so they're omitted here. Editing Personal Info / Contact
 * is offered only when `Patient Module / Patient Profile = edit` and reuses the
 * workspace's own EditPatientDetailsDrawer.
 *
 * Reuses the genuinely-standalone cards (PatientBillingSummaryCard,
 * FamilyHistorySection) directly; the rest are compact read-only cards.
 */
import { type CSSProperties, type ReactNode, useState } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { usePermissions } from '@helios/auth-firebase-web';
import { qPatientAllergies, type PatientAllergyRow } from '@helios/data-access';
import { PERM } from '../auth/permissionMap';
import { usePowerSyncQuery } from '../adapters/powersync';
import { NavIcon, useBrandTokens } from './shared';
import { getPatientColor } from './PatientTabsContext';
import { usePatientDetailsEditor } from './usePatientDetailsEditor';
import { EditPatientDetailsDrawer, type EditDrawerMode } from './EditPatientDetailsDrawer';
import { PatientAgeDobConflictModal } from './patient-age-conflict'; // PRD.315
import { toPatientAgeView } from './patient-age-display'; // PRD.315
import { PatientBillingSummaryCard } from '../pages/billing/PatientBillingSummaryCard';
import { usePatientBilling } from '../pages/billing/usePatientBilling';
import { useFormatMoney } from '../hooks/useFormatMoney';
import { FamilyHistorySection } from './FamilyHistoryView';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string | null;
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

export function PatientDetailsDrawer({ open, onOpenChange, patientId }: Props) {
  const brandTokens = useBrandTokens();
  const { canAccess } = usePermissions();
  const canEdit = canAccess(PERM.patientProfileEdit);
  const fmtMoney = useFormatMoney();

  const editor = usePatientDetailsEditor(patientId ?? '');
  const { patient, contactRow, emergencyContact } = editor;
  const billing = usePatientBilling(patientId);
  const { data: allergies = [] } = usePowerSyncQuery<PatientAllergyRow>(
    qPatientAllergies(patientId ?? ''),
  );

  const [editMode, setEditMode] = useState<EditDrawerMode | null>(null);

  const name = patient?.full_name ?? 'Unknown';
  const age = toPatientAgeView(patient);
  const sex = patient?.sex ?? '';
  const status = patient?.status ?? 'active';
  const mobile = [contactRow?.mobile_country_code, contactRow?.mobile_number]
    .map((s) => (s ?? '').trim())
    .filter(Boolean)
    .join(' ');
  const phone = mobile || contactRow?.formal_telephone || '';
  const email = contactRow?.email ?? '';
  const address = [
    contactRow?.formal_residential_address,
    contactRow?.formal_district,
    contactRow?.formal_province_state,
    contactRow?.formal_postal_code,
  ]
    .map((s) => (s ?? '').trim())
    .filter(Boolean)
    .join(', ');

  const editDetails = canEdit ? () => setEditMode('details') : undefined;
  const editContacts = canEdit ? () => setEditMode('contacts') : undefined;

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        title="Patient Details"
        description={
          canEdit ? 'View or edit this patient.' : 'You have view-only access to this patient.'
        }
        width={420}
        portalStyle={brandTokens as CSSProperties}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-4)',
            padding: 'var(--sp-4)',
            background: 'var(--bg-canvas, var(--bg-surface-2))',
            minHeight: '100%',
          }}
        >
          {!patient ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
              Loading…
            </div>
          ) : (
            <>
              {/* Header card — avatar + identity + Balance/Status chips */}
              <div style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)' }}>
                  {patient.photo_url ? (
                    <img
                      src={patient.photo_url}
                      alt={name}
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: 'var(--radius-full)',
                        objectFit: 'cover',
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: 'var(--radius-full)',
                        background: getPatientColor(patient.id),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        fontSize: 'var(--font-size-lg)',
                        fontWeight: 'var(--font-weight-semi)',
                        color: '#fff',
                      }}
                    >
                      {getInitials(name)}
                    </div>
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: 'var(--font-size-lg)',
                        fontWeight: 'var(--font-weight-semi)',
                        color: 'var(--text)',
                      }}
                    >
                      {name}
                    </div>
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--text-muted)',
                        marginTop: 2,
                      }}
                    >
                      {[
                        patient.patient_number ? `P-${patient.patient_number}` : '',
                        age.compact,
                        sex ? capitalize(sex) : '',
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                    {/* PRD.315 §8.2 — a reported age is a dated observation, so
                        say when it was reported instead of implying it is current. */}
                    {age.provenance && (
                      <div
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text-muted)',
                          marginTop: 2,
                        }}
                      >
                        {age.provenance}
                      </div>
                    )}
                  </div>
                  {editDetails && <EditPill onClick={editDetails} />}
                </div>
                {/* Balance + Status chips */}
                <div style={{ display: 'flex', gap: 'var(--sp-3)', marginTop: 'var(--sp-4)' }}>
                  <Chip
                    label="Balance"
                    value={fmtMoney(billing.balance)}
                    valueColor={billing.balanceCents > 0 ? '#dc2626' : '#16a34a'}
                  />
                  <Chip
                    label="Status"
                    value={capitalize(status)}
                    dot={status.toLowerCase() === 'active' ? '#16a34a' : 'var(--text-muted)'}
                  />
                </div>
              </div>

              {/* Patient Details (contact) */}
              <Card icon="user" title="Patient Details" onEdit={editContacts}>
                <Row icon="map-pin" value={address || 'No address specified'} muted={!address} />
                <Row icon="phone" value={phone || 'No phone specified'} muted={!phone} />
                <Row icon="mail" value={email || 'No email specified'} muted={!email} />
              </Card>

              {/* Billing — reuse the standalone summary card */}
              <PatientBillingSummaryCard patientId={patientId} />

              {/* Allergies (read-only) */}
              <Card icon="alert-triangle" title="Allergies">
                {allergies.length === 0 ? (
                  <div style={mutedText}>No known allergies</div>
                ) : (
                  allergies.map((a) => (
                    <Row
                      key={a.id}
                      icon="alert-triangle"
                      value={
                        [a.substance, a.reaction, a.severity].filter(Boolean).join(' · ') ||
                        (a.allergy_type ?? 'Allergy')
                      }
                    />
                  ))
                )}
              </Card>

              {/* Emergency contact */}
              {emergencyContact && (emergencyContact.name || emergencyContact.phone) && (
                <Card icon="user" title="Emergency Contact" onEdit={editContacts}>
                  <Row
                    icon="user"
                    value={`${emergencyContact.name ?? ''}${emergencyContact.relationship ? ` (${emergencyContact.relationship})` : ''}`}
                  />
                  <Row icon="phone" value={emergencyContact.phone ?? ''} />
                </Card>
              )}

              {/* Family history — reuse the standalone card (read-only) */}
              <FamilyHistorySection patientId={patient.id} variant="card" />
            </>
          )}
        </div>
      </Drawer>

      {/* Edit drawer — reuses the exact form the Patient Workspace uses. */}
      {canEdit && (
        <EditPatientDetailsDrawer
          open={editMode != null}
          onOpenChange={(o) => {
            if (!o) setEditMode(null);
          }}
          brandTokens={brandTokens}
          mode={editMode ?? 'details'}
          questionnaire={
            editMode === 'contacts' ? editor.contactsQuestionnaire : editor.detailsQuestionnaire
          }
          initialAnswers={
            editMode === 'contacts' ? editor.initialContactsAnswers : editor.initialDetailsAnswers
          }
          validationSchema={
            editMode === 'contacts'
              ? editor.contactsValidationSchema
              : editor.detailsValidationSchema
          }
          onSave={editMode === 'contacts' ? editor.handleContactsSave : editor.handleDetailsSave}
          confirmBeforeSave={editMode === 'contacts' ? undefined : editor.confirmDetailsSave}
          fieldRenderers={editMode === 'contacts' ? editor.contactsFieldRenderers : undefined}
          patientId={patientId ?? undefined}
        />
      )}

      {/* PRD.315 — shown only when the entered DOB contradicts the reported age. */}
      <PatientAgeDobConflictModal {...editor.ageConflict.modalProps} brandTokens={brandTokens} />
    </>
  );
}

// ─── Presentational helpers (match the sidebar Overview card look) ───

const cardStyle: CSSProperties = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg, 12px)',
  padding: 'var(--sp-4)',
};

const mutedText: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text-muted)',
};

function Card({
  icon,
  title,
  onEdit,
  children,
}: {
  icon: string;
  title: string;
  onEdit?: () => void;
  children: ReactNode;
}) {
  return (
    <div style={cardStyle}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--sp-3)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          <span style={{ color: 'var(--text)', display: 'flex' }}>
            <NavIcon name={icon} />
          </span>
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            {title}
          </span>
        </div>
        {onEdit && <EditPill onClick={onEdit} />}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>{children}</div>
    </div>
  );
}

function Row({ icon, value, muted }: { icon: string; value: string; muted?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-3)' }}>
      <span style={{ color: 'var(--text-muted)', display: 'flex', marginTop: 1 }}>
        <NavIcon name={icon} />
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: muted ? 'var(--text-muted)' : 'var(--text)',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function EditPill({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        background: 'transparent',
        color: 'var(--color-primary)',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        fontFamily: 'inherit',
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      <NavIcon name="edit" />
      Edit
    </button>
  );
}

function Chip({
  label,
  value,
  valueColor,
  dot,
}: {
  label: string;
  value: string;
  valueColor?: string;
  dot?: string;
}) {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        padding: 'var(--sp-2)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-surface-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {dot && <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot }} />}
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            color: valueColor ?? 'var(--text)',
          }}
        >
          {value}
        </span>
      </div>
      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>{label}</span>
    </div>
  );
}
